import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, HeartIcon, CloudIcon, SunIcon, SparklesIcon, MusicIcon, BookOpenIcon, PaletteIcon } from 'lucide-react';
// Wavy divider SVG component
function WavyDivider({
  color,
  flip = false
}: {
  color: string;
  flip?: boolean;
}) {
  return <div className={`w-full overflow-hidden ${flip ? 'rotate-180' : ''}`} style={{
    marginTop: '-1px',
    marginBottom: '-1px'
  }} data-id="element-0">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-16 md:h-24" fill={color} data-id="element-1">
        <path d="M0,60 C150,120 350,0 600,60 C850,120 1050,0 1200,60 L1200,120 L0,120 Z" data-id="element-2" />
      </svg>
    </div>;
}
// Hand-drawn style blob background
function BlobShape({
  className,
  color
}: {
  className?: string;
  color: string;
}) {
  return <svg viewBox="0 0 200 200" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-3">
      <path fill={color} d="M47.5,-57.2C59.9,-45.8,67.5,-29.5,70.4,-12.3C73.3,4.9,71.5,23,62.8,36.8C54.1,50.6,38.5,60.1,21.6,66.1C4.7,72.1,-13.5,74.6,-29.8,69.3C-46.1,64,-60.5,50.9,-68.2,34.5C-75.9,18.1,-76.9,-1.6,-71.3,-18.8C-65.7,-36,-53.5,-50.7,-39.1,-61.7C-24.7,-72.7,-8.1,-80,5.8,-86.8C19.7,-93.6,35.1,-68.6,47.5,-57.2Z" transform="translate(100 100)" data-id="element-4" />
    </svg>;
}
// Cute sun illustration
function SunIllustration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-5">
      <circle cx="50" cy="50" r="25" fill="#FFE252" data-id="element-6" />
      <circle cx="43" cy="45" r="3" fill="#5D4E37" data-id="element-7" />
      <circle cx="57" cy="45" r="3" fill="#5D4E37" data-id="element-8" />
      <path d="M42 58 Q50 65 58 58" stroke="#5D4E37" strokeWidth="2.5" fill="none" strokeLinecap="round" data-id="element-9" />
      {/* Rays */}
      <g stroke="#FFE252" strokeWidth="3" strokeLinecap="round" data-id="element-10">
        <line x1="50" y1="15" x2="50" y2="8" data-id="element-11" />
        <line x1="50" y1="92" x2="50" y2="85" data-id="element-12" />
        <line x1="15" y1="50" x2="8" y2="50" data-id="element-13" />
        <line x1="92" y1="50" x2="85" y2="50" data-id="element-14" />
        <line x1="25" y1="25" x2="20" y2="20" data-id="element-15" />
        <line x1="75" y1="25" x2="80" y2="20" data-id="element-16" />
        <line x1="25" y1="75" x2="20" y2="80" data-id="element-17" />
        <line x1="75" y1="75" x2="80" y2="80" data-id="element-18" />
      </g>
    </svg>;
}
// Cute cloud illustration
function CloudIllustration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 120 70" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-19">
      <ellipse cx="60" cy="45" rx="45" ry="20" fill="white" data-id="element-20" />
      <circle cx="35" cy="35" r="20" fill="white" data-id="element-21" />
      <circle cx="60" cy="28" r="22" fill="white" data-id="element-22" />
      <circle cx="85" cy="35" r="18" fill="white" data-id="element-23" />
      {/* Cute face */}
      <circle cx="50" cy="40" r="2" fill="#B0C4DE" data-id="element-24" />
      <circle cx="70" cy="40" r="2" fill="#B0C4DE" data-id="element-25" />
      <path d="M55 48 Q60 52 65 48" stroke="#B0C4DE" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-26" />
    </svg>;
}
// Star decoration
function StarDecoration({
  className,
  delay = 0
}: {
  className?: string;
  delay?: number;
}) {
  return <motion.svg viewBox="0 0 50 50" className={className} initial={{
    scale: 0,
    rotate: -180
  }} animate={{
    scale: 1,
    rotate: 0
  }} transition={{
    delay,
    duration: 0.5,
    type: 'spring'
  }} data-id="element-27">
      <path d="M25 5 L30 20 L45 20 L33 30 L38 45 L25 35 L12 45 L17 30 L5 20 L20 20 Z" fill="#FFE252" stroke="#FFCC00" strokeWidth="1" data-id="element-28" />
    </motion.svg>;
}
// Program card component
function ProgramCard({
  icon: Icon,
  title,
  description,
  price,
  color,
  delay
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  price: string;
  color: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.5
  }} whileHover={{
    y: -8,
    transition: {
      duration: 0.2
    }
  }} className="relative" data-id="element-29">
      <div className="relative p-6 md:p-8 rounded-[2rem] shadow-soft-lg" style={{
      backgroundColor: color
    }} data-id="element-30">
        {/* Decorative blob in corner */}
        <div className="absolute -top-4 -right-4 w-16 h-16 opacity-30" data-id="element-31">
          <BlobShape color="white" data-id="element-32" />
        </div>

        <motion.div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/60 flex items-center justify-center mb-4 mx-auto" whileHover={{
        rotate: [0, -10, 10, 0],
        transition: {
          duration: 0.5
        }
      }} data-id="element-33">
          <Icon className="w-8 h-8 md:w-10 md:h-10 text-gray-700" strokeWidth={1.5} data-id="element-34" />
        </motion.div>

        <h3 className="font-fredoka text-xl md:text-2xl font-semibold text-gray-800 text-center mb-2" data-id="element-35">
          {title}
        </h3>
        <p className="font-baloo text-gray-700 text-center mb-4 text-sm md:text-base" data-id="element-36">
          {description}
        </p>
        <div className="text-center" data-id="element-37">
          <span className="font-fredoka text-2xl md:text-3xl font-bold text-gray-800" data-id="element-38">
            {price}
          </span>
          <span className="font-baloo text-gray-600 text-sm" data-id="element-39"> / miesiąc</span>
        </div>
      </div>
    </motion.div>;
}
export function App() {
  return <div className="min-h-screen w-full bg-cream overflow-x-hidden" data-id="element-40">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 py-16 overflow-hidden bg-gradient-to-b from-peach-100 to-cream" data-id="element-41">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-42">
          <motion.div className="absolute top-10 left-10 w-24 md:w-32" animate={{
          y: [0, -15, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-43">
            <SunIllustration className="w-full h-full" data-id="element-44" />
          </motion.div>

          <motion.div className="absolute top-20 right-10 md:right-20 w-28 md:w-40" animate={{
          x: [0, 20, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-45">
            <CloudIllustration className="w-full h-full" data-id="element-46" />
          </motion.div>

          <motion.div className="absolute bottom-40 left-5 md:left-20 w-20 md:w-32" animate={{
          x: [0, -15, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} data-id="element-47">
            <CloudIllustration className="w-full h-full opacity-80" data-id="element-48" />
          </motion.div>

          <StarDecoration className="absolute top-32 left-1/4 w-8 md:w-12" delay={0.2} data-id="element-49" />
          <StarDecoration className="absolute top-48 right-1/4 w-6 md:w-10" delay={0.4} data-id="element-50" />
          <StarDecoration className="absolute bottom-32 right-1/3 w-10 md:w-14" delay={0.6} data-id="element-51" />

          {/* Large blob decorations */}
          <div className="absolute -bottom-20 -left-20 w-64 md:w-96 opacity-30" data-id="element-52">
            <BlobShape color="#FFD4C4" data-id="element-53" />
          </div>
          <div className="absolute -top-20 -right-20 w-48 md:w-72 opacity-20" data-id="element-54">
            <BlobShape color="#C4F4DD" data-id="element-55" />
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto" data-id="element-56">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-57">
            <h1 className="font-fredoka text-4xl sm:text-5xl md:text-7xl font-bold text-gray-800 mb-6 leading-tight" data-id="element-58">
              Witaj w{' '}
              <span className="relative inline-block" data-id="element-59">
                <span className="relative z-10 text-peach-500" data-id="element-60">Krainie</span>
                <motion.span className="absolute -bottom-2 left-0 right-0 h-4 bg-butterYellow-200 -z-0 rounded-full" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                delay: 0.5,
                duration: 0.4
              }} data-id="element-61" />
              </span>{' '}
              Marzeń!
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.6
        }} className="font-baloo text-lg md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-62">
            Magiczne miejsce, gdzie każde dziecko odkrywa radość nauki, zabawy i
            przyjaźni w ciepłej, rodzinnej atmosferze ✨
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4,
          duration: 0.4
        }} data-id="element-63">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="font-fredoka text-lg md:text-xl font-semibold px-8 md:px-12 py-4 md:py-5 bg-mint-300 text-gray-800 rounded-full shadow-soft-lg hover:bg-mint-400 transition-colors" data-id="element-64">
              Poznaj nas bliżej 🌟
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Wavy divider */}
      <WavyDivider color="#C4F4DD" data-id="element-65" />

      {/* Programs Section */}
      <section className="relative py-16 md:py-24 px-4 bg-mint-200" data-id="element-66">
        <div className="max-w-6xl mx-auto" data-id="element-67">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12 md:mb-16" data-id="element-68">
            <h2 className="font-fredoka text-3xl md:text-5xl font-bold text-gray-800 mb-4" data-id="element-69">
              Nasze Programy 🎨
            </h2>
            <p className="font-baloo text-lg md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-70">
              Każdy program to nowa przygoda pełna odkryć i radości!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-71">
            <ProgramCard icon={SparklesIcon} title="Mały Odkrywca" description="Zabawy sensoryczne i eksperymenty dla najmłodszych badaczy świata" price="450 zł" color="#FFD4C4" delay={0} data-id="element-72" />
            <ProgramCard icon={PaletteIcon} title="Artystyczna Dusza" description="Malowanie, lepienie i tworzenie - rozwijamy kreatywność!" price="520 zł" color="#E4D4F4" delay={0.1} data-id="element-73" />
            <ProgramCard icon={MusicIcon} title="Muzyczne Nutki" description="Śpiew, taniec i nauka rytmu w radosnej atmosferze" price="480 zł" color="#FFF4C4" delay={0.2} data-id="element-74" />
            <ProgramCard icon={BookOpenIcon} title="Bajkowy Świat" description="Czytanie, teatrzyk i rozwijanie wyobraźni przez opowieści" price="420 zł" color="#C4F4DD" delay={0.3} data-id="element-75" />
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 right-10 opacity-20 pointer-events-none" data-id="element-76">
          <BlobShape className="w-32 md:w-48" color="#E4D4F4" data-id="element-77" />
        </div>
      </section>

      {/* Wavy divider */}
      <WavyDivider color="#E4D4F4" data-id="element-78" />

      {/* About Section */}
      <section className="relative py-16 md:py-24 px-4 bg-lavender-200" data-id="element-79">
        <div className="max-w-4xl mx-auto" data-id="element-80">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-white/70 backdrop-blur-sm rounded-[2.5rem] p-8 md:p-12 shadow-soft-lg" data-id="element-81">
            {/* Decorative corners */}
            <div className="absolute -top-6 -left-6" data-id="element-82">
              <motion.div animate={{
              rotate: [0, 10, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity
            }} data-id="element-83">
                <HeartIcon className="w-12 h-12 text-peach-300 fill-peach-200" data-id="element-84" />
              </motion.div>
            </div>
            <div className="absolute -bottom-6 -right-6" data-id="element-85">
              <motion.div animate={{
              rotate: [0, -10, 10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              delay: 0.5
            }} data-id="element-86">
                <StarIcon className="w-12 h-12 text-butterYellow-400 fill-butterYellow-200" data-id="element-87" />
              </motion.div>
            </div>

            <h2 className="font-fredoka text-3xl md:text-5xl font-bold text-gray-800 mb-6 text-center" data-id="element-88">
              Nasza Historia 📖
            </h2>

            <div className="font-baloo text-gray-700 text-lg md:text-xl leading-relaxed space-y-6" data-id="element-89">
              <p data-id="element-90">
                <span className="text-2xl" data-id="element-91">D</span>awno, dawno temu, w sercu
                Warszawy, powstało magiczne miejsce, gdzie marzenia dzieci stają
                się rzeczywistością...
              </p>
              <p data-id="element-92">
                Nasza Kraina Marzeń to więcej niż przedszkole - to ciepły dom,
                gdzie każde dziecko jest wyjątkowe. Wierzymy, że nauka przez
                zabawę to najpiękniejsza przygoda, jaką można przeżyć!
              </p>
              <p data-id="element-93">
                Z miłością i troską towarzyszymy maluchom w ich pierwszych
                krokach ku wielkiemu światu, budując fundamenty przyjaźni,
                ciekawości i radości.
                <span className="text-peach-500" data-id="element-94"> 💕</span>
              </p>
            </div>

            <motion.div className="mt-8 flex justify-center gap-4 flex-wrap" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-95">
              {['Miłość', 'Zabawa', 'Rozwój', 'Przyjaźń'].map((value, i) => <span key={value} className="font-fredoka px-4 py-2 rounded-full text-sm md:text-base font-medium" style={{
              backgroundColor: ['#FFD4C4', '#C4F4DD', '#FFF4C4', '#E4D4F4'][i],
              color: '#4A4A4A'
            }} data-id="element-96">
                  {value}
                </span>)}
            </motion.div>
          </motion.div>
        </div>

        {/* Floating decorations */}
        <motion.div className="absolute bottom-20 left-10 pointer-events-none" animate={{
        y: [0, -20, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity
      }} data-id="element-97">
          <CloudIllustration className="w-24 md:w-32 opacity-60" data-id="element-98" />
        </motion.div>
      </section>

      {/* Wavy divider */}
      <WavyDivider color="#FFF4C4" data-id="element-99" />

      {/* Footer */}
      <footer className="relative py-12 md:py-16 px-4 bg-butterYellow-200" data-id="element-100">
        <div className="max-w-4xl mx-auto text-center" data-id="element-101">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-102">
            <h3 className="font-fredoka text-2xl md:text-3xl font-bold text-gray-800 mb-4" data-id="element-103">
              Kraina Marzeń 🌈
            </h3>

            <div className="font-baloo text-gray-700 space-y-2 mb-8" data-id="element-104">
              <p className="text-lg md:text-xl flex items-center justify-center gap-2" data-id="element-105">
                <span className="text-peach-400" data-id="element-106">📍</span>
                Hoża 29, 00-521 Warszawa
              </p>
              <p className="text-base md:text-lg" data-id="element-107">
                <span className="text-mint-500" data-id="element-108">📞</span> +48 22 123 45 67
              </p>
              <p className="text-base md:text-lg" data-id="element-109">
                <span className="text-lavender-400" data-id="element-110">✉️</span>{' '}
                kontakt@krainamarzen.pl
              </p>
            </div>

            <div className="flex justify-center gap-6 mb-8" data-id="element-111">
              {[HeartIcon, StarIcon, CloudIcon, SunIcon].map((Icon, i) => <motion.div key={i} whileHover={{
              scale: 1.2,
              rotate: 10
            }} className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: ['#FFD4C4', '#FFF4C4', '#C4F4DD', '#E4D4F4'][i]
            }} data-id="element-112">
                  <Icon className="w-5 h-5 text-gray-700" data-id="element-113" />
                </motion.div>)}
            </div>

            <p className="font-baloo text-gray-600 text-sm" data-id="element-114">
              © 2024 Kraina Marzeń. Wszystkie marzenia zastrzeżone ✨
            </p>
          </motion.div>
        </div>

        {/* Bottom decorative wave */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none" data-id="element-115">
          <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-8" fill="#FFFBF5" data-id="element-116">
            <path d="M0,30 Q300,60 600,30 T1200,30 L1200,60 L0,60 Z" data-id="element-117" />
          </svg>
        </div>
      </footer>
    </div>;
}